/**
 * Centralized Error Handler for AfterService digital assistant
 * Provides consistent error handling, logging, and user feedback
 * Maintains security by not exposing technical details to users
 */

import { getMonitoringConfig } from '../config/config';

// Error severity levels
export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// Error categories
export const ERROR_CATEGORIES = {
  AUTHENTICATION: 'authentication',
  NETWORK: 'network',
  API: 'api',
  VALIDATION: 'validation',
  CONFIGURATION: 'configuration',
  PERFORMANCE: 'performance',
  UNKNOWN: 'unknown'
};

// User-friendly error messages
const ERROR_MESSAGES = {
  [ERROR_CATEGORIES.AUTHENTICATION]: {
    default: 'Please sign in again to continue.',
    expired: 'Your session has expired. Please sign in again.',
    unauthorized: 'You do not have permission to perform this action.',
    forbidden: 'Access denied. Please contact support if you believe this is an error.'
  },
  [ERROR_CATEGORIES.NETWORK]: {
    default: 'Network connection issue. Please check your internet and try again.',
    timeout: 'Request timed out. Please try again.',
    offline: 'You appear to be offline. Please check your internet connection.',
    dns: 'Unable to connect to the service. Please try again later.'
  },
  [ERROR_CATEGORIES.API]: {
    default: 'Something went wrong. Please try again.',
    serverError: 'Service temporarily unavailable. Please try again in a few minutes.',
    badRequest: 'Invalid request. Please check your input and try again.',
    notFound: 'The requested resource was not found.',
    rateLimit: 'Too many requests. Please wait a moment before trying again.'
  },
  [ERROR_CATEGORIES.VALIDATION]: {
    default: 'Please check your input and try again.',
    required: 'Please fill in all required fields.',
    format: 'Please check the format of your input.',
    length: 'Input length is invalid. Please check the requirements.'
  },
  [ERROR_CATEGORIES.CONFIGURATION]: {
    default: 'System configuration issue. Please contact support.',
    missing: 'Required configuration is missing. Please contact support.',
    invalid: 'Invalid configuration detected. Please contact support.'
  },
  [ERROR_CATEGORIES.PERFORMANCE]: {
    default: 'Performance issue detected. Please try again.',
    slowResponse: 'Service is responding slowly. Please be patient.',
    highLoad: 'Service is experiencing high load. Please try again later.'
  },
  [ERROR_CATEGORIES.UNKNOWN]: {
    default: 'An unexpected error occurred. Please try again or contact support.'
  }
};

class ErrorHandler {
  constructor() {
    this.monitoringConfig = getMonitoringConfig();
    this.errorQueue = [];
    this.maxQueueSize = 100;
  }

  /**
   * Categorize error based on error details
   */
  categorizeError(error) {
    const errorString = error.toString().toLowerCase();
    const errorCode = error.code || error.status;

    // Authentication errors
    if (errorString.includes('unauthorized') || errorString.includes('authentication') || errorCode === 401) {
      return ERROR_CATEGORIES.AUTHENTICATION;
    }

    // Network errors
    if (errorString.includes('network') || errorString.includes('fetch') || errorString.includes('timeout')) {
      return ERROR_CATEGORIES.NETWORK;
    }

    // API errors
    if (errorCode >= 400 && errorCode < 600) {
      return ERROR_CATEGORIES.API;
    }

    // Validation errors
    if (errorString.includes('validation') || errorString.includes('invalid')) {
      return ERROR_CATEGORIES.VALIDATION;
    }

    // Configuration errors
    if (errorString.includes('configuration') || errorString.includes('config')) {
      return ERROR_CATEGORIES.CONFIGURATION;
    }

    // Performance errors
    if (errorString.includes('performance') || errorString.includes('slow')) {
      return ERROR_CATEGORIES.PERFORMANCE;
    }

    return ERROR_CATEGORIES.UNKNOWN;
  }

  /**
   * Determine error severity
   */
  determineSeverity(error, category) {
    const errorCode = error.code || error.status;

    // Critical errors
    if (category === ERROR_CATEGORIES.CONFIGURATION || errorCode >= 500) {
      return ERROR_SEVERITY.CRITICAL;
    }

    // High severity errors
    if (category === ERROR_CATEGORIES.AUTHENTICATION || errorCode === 403) {
      return ERROR_SEVERITY.HIGH;
    }

    // Medium severity errors
    if (category === ERROR_CATEGORIES.API || category === ERROR_CATEGORIES.NETWORK) {
      return ERROR_SEVERITY.MEDIUM;
    }

    // Low severity errors
    return ERROR_SEVERITY.LOW;
  }

  /**
   * Get user-friendly error message
   */
  getUserFriendlyMessage(error, category) {
    const errorCode = error.code || error.status;
    const errorString = error.toString().toLowerCase();
    const categoryMessages = ERROR_MESSAGES[category] || ERROR_MESSAGES[ERROR_CATEGORIES.UNKNOWN];

    // Specific error code handling
    switch (errorCode) {
      case 401:
        return categoryMessages.expired || categoryMessages.default;
      case 403:
        return categoryMessages.forbidden || categoryMessages.default;
      case 404:
        return ERROR_MESSAGES[ERROR_CATEGORIES.API].notFound;
      case 429:
        return ERROR_MESSAGES[ERROR_CATEGORIES.API].rateLimit;
      case 500:
      case 502:
      case 503:
      case 504:
        return ERROR_MESSAGES[ERROR_CATEGORIES.API].serverError;
      default:
        break;
    }

    // Content-based message selection
    if (errorString.includes('timeout')) {
      return ERROR_MESSAGES[ERROR_CATEGORIES.NETWORK].timeout;
    }
    if (errorString.includes('offline')) {
      return ERROR_MESSAGES[ERROR_CATEGORIES.NETWORK].offline;
    }
    if (errorString.includes('expired')) {
      return ERROR_MESSAGES[ERROR_CATEGORIES.AUTHENTICATION].expired;
    }

    return categoryMessages.default;
  }

  /**
   * Log error securely
   */
  logError(errorInfo) {
    const { error, category, severity, context, userMessage, canRetry } = errorInfo;

    // Always log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.group(`🚨 ${severity.toUpperCase()} ERROR [${category}]`);
      console.error('Context:', context);
      console.error('User Message:', userMessage);
      console.error('Can Retry:', canRetry);
      console.error('Original Error:', error);
      console.groupEnd();
    }

    // Production logging (no sensitive data)
    if (this.monitoringConfig.enableErrorReporting) {
      const logEntry = {
        timestamp: new Date().toISOString(),
        category,
        severity,
        context,
        userMessage,
        canRetry,
        errorCode: error.code || error.status,
        errorType: error.name || 'Error',
        // Note: We don't log the full error message in production
        hasStackTrace: !!error.stack,
        userAgent: navigator.userAgent,
        url: window.location.href
      };

      // Add to error queue for batch reporting
      this.addToErrorQueue(logEntry);
    }
  }

  /**
   * Add error to queue for batch reporting
   */
  addToErrorQueue(errorEntry) {
    this.errorQueue.push(errorEntry);

    // Maintain queue size
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }

    // TODO: Implement batch error reporting to CloudWatch or external service
    // This would be triggered periodically or when queue reaches certain size
  }

  /**
   * Determine if error is retryable
   */
  canRetry(error, category) {
    const errorCode = error.code || error.status;

    // Never retry authentication errors
    if (category === ERROR_CATEGORIES.AUTHENTICATION) {
      return false;
    }

    // Never retry client errors (400-499) except for timeouts and rate limits
    if (errorCode >= 400 && errorCode < 500 && errorCode !== 408 && errorCode !== 429) {
      return false;
    }

    // Retry network errors and server errors
    if (category === ERROR_CATEGORIES.NETWORK || errorCode >= 500) {
      return true;
    }

    // Retry rate limit errors
    if (errorCode === 429) {
      return true;
    }

    return true;
  }

  /**
   * Get retry delay based on error type
   */
  getRetryDelay(error, category, attempt = 1) {
    const baseDelay = 1000; // 1 second
    const maxDelay = 30000; // 30 seconds

    // Exponential backoff with jitter
    const delay = Math.min(baseDelay * Math.pow(2, attempt - 1), maxDelay);
    const jitter = Math.random() * 0.1 * delay;
    
    return Math.floor(delay + jitter);
  }

  /**
   * Main error handling method
   */
  handleError(error, context = 'unknown') {
    try {
      const category = this.categorizeError(error);
      const severity = this.determineSeverity(error, category);
      const userMessage = this.getUserFriendlyMessage(error, category);
      const canRetry = this.canRetry(error, category);

      const errorInfo = {
        error,
        category,
        severity,
        context,
        userMessage,
        canRetry,
        timestamp: new Date().toISOString(),
        retryDelay: canRetry ? this.getRetryDelay(error, category) : null
      };

      // Log the error
      this.logError(errorInfo);

      return errorInfo;
    } catch (handlingError) {
      // Fallback error handling
      console.error('❌ Error handler failed:', handlingError);
      return {
        error,
        category: ERROR_CATEGORIES.UNKNOWN,
        severity: ERROR_SEVERITY.MEDIUM,
        context,
        userMessage: 'An unexpected error occurred. Please try again.',
        canRetry: true,
        timestamp: new Date().toISOString(),
        retryDelay: 2000
      };
    }
  }

  /**
   * Handle API errors specifically
   */
  handleApiError(error, endpoint = 'unknown') {
    return this.handleError(error, `api:${endpoint}`);
  }

  /**
   * Handle authentication errors specifically
   */
  handleAuthError(error, action = 'unknown') {
    return this.handleError(error, `auth:${action}`);
  }

  /**
   * Handle network errors specifically
   */
  handleNetworkError(error, operation = 'unknown') {
    return this.handleError(error, `network:${operation}`);
  }

  /**
   * Handle validation errors specifically
   */
  handleValidationError(error, field = 'unknown') {
    return this.handleError(error, `validation:${field}`);
  }

  /**
   * Get error queue for reporting
   */
  getErrorQueue() {
    return [...this.errorQueue];
  }

  /**
   * Clear error queue
   */
  clearErrorQueue() {
    this.errorQueue = [];
  }
}

// Create singleton instance
const errorHandler = new ErrorHandler();

// Export convenience methods
export const handleError = (error, context) => errorHandler.handleError(error, context);
export const handleApiError = (error, endpoint) => errorHandler.handleApiError(error, endpoint);
export const handleAuthError = (error, action) => errorHandler.handleAuthError(error, action);
export const handleNetworkError = (error, operation) => errorHandler.handleNetworkError(error, operation);
export const handleValidationError = (error, field) => errorHandler.handleValidationError(error, field);

// Export error handler instance
export default errorHandler; 